// TL-5 Select: hover pre-highlight, click/Shift-click, window/crossing marquee, drag-move with
// Ctrl-copy at drop, grips (polyline vertex/midpoint-insert, circle center/radius, arc ends/bulge),
// arrow nudge, delete. Gestures build ONE command, dispatched at pointerup (DM-7).
import { angleOf, dir, dist, normalizeDeg, sub, type Vec } from "../core/geometry";
import { formatLength, parseScalar } from "../core/units";
import type { Doc, Entity, PolylineEntity } from "../doc/entities";
import { makeDeleteChildrenCommand, makeDeleteCommand } from "../doc/commands";
import { hitTest, marqueeTest, HIT_TOL_PX } from "../engine/hitTest";
import { drawEntity, tracePath, type RenderView } from "../engine/renderEntities";
import { applyCommand } from "../doc/commands";
import { drawReadout } from "./line";
import { drawImageEntity } from "../engine/renderImages";
import { imageCenter, imageCorners } from "../engine/imageGeom";
import { arcPoint, distPointSeg } from "../core/intersect";
import { worldToScreen } from "../engine/camera";
import {
  duplicateCommand,
  expandGroupChildren,
  isLocked,
  moveCommand,
  rotatePatches,
  scalePatches,
  translatePatches
} from "./entityOps";
import { entityBounds } from "../engine/bounds";
import type { Bounds } from "../core/geometry";
import { arcFrom3Points } from "./arc";
import { anchorFromSnap, offsetFor } from "./dimension";
import { dimensionGeometry, labelTextBox } from "../doc/derived";
import { resolveAnchor } from "../doc/anchors";
import type { SnapResult } from "../engine/snap";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";
import { pivotOf, setPivotPatch } from "./pivot";
import { allPorts, joinedEndStyle, pipeEnds, pipeMetaOf, setPipePatches, type Port } from "../pipes/api";
import { connectedRun } from "../pipes/network";
import { combine, drawPipeGhost, drawPorts, freePortNear, joinPatches, looksLikeFitting, looksLikePipe, ownerName, safePorts, unjoinPatches } from "./pipeTool";
import { alignMove, type AlignGuide, type AlignInput, type Box } from "./alignSnap";
import { doorAt, doorOutline, movedWallPoints, removeDoorCommand, setWallPointsCommand, slideDoorAt, updateDoorCommand, wallAttachPoint } from "../walls/edit";
import { doorSpan, spineOf, wallGeometry, wallMetaOf, wallPartsInDoc } from "../walls/wall";

/** The mark on a picked door (3.21.0): the selection blue, dashed. */
const DOOR_MARK = "#4d9fe1";

/**
 * A COMPONENT: a group that TraceCAD made and reads back (a fitting, a pump, a lint pot, a pipe). Its lines are one
 * object: they are never edited one by one (owner, 2026-10-20: a line inside a lint pot was selected and moved, the
 * pot looked broken into pieces, and every click then picked one line). Change a component in the Inspector; ungroup
 * it to edit its lines (it is then plain lines).
 */
export function isComponent(e: Entity | undefined): boolean {
  return e?.type === "group" && !!e.meta && !!(e.meta.fitting || e.meta.pump || e.meta.pipe || e.meta.lintpot || e.meta.ports);
}

/** Alignment guides: snap colour, dashed. */
const GUIDE = "#ffb74d";
/** Snaps to a real point: these beat the alignment while moving. */
const POINT_SNAPS = new Set<string>(["endpoint", "intersection", "midpoint", "center", "quadrant", "gridEntity"]);
/** How near (screen pixels) an end or an edge must come before the move lines it up. */
const ALIGN_PX = 9;

const GRIP_PX = 8;

type Grip =
  | { kind: "vertex"; i: number; p: Vec }
  | { kind: "midInsert"; i: number; p: Vec }
  | { kind: "center"; p: Vec }
  | { kind: "radius"; p: Vec }
  | { kind: "arcStart"; p: Vec }
  | { kind: "arcEnd"; p: Vec }
  | { kind: "arcBulge"; p: Vec }
  | { kind: "imgCorner"; i: number; p: Vec } // TL-5: corner scale (aspect locked; Shift = free)
  | { kind: "imgRotate"; p: Vec }
  | { kind: "labelHead"; p: Vec } // ANN-2: head and tail drag independently
  | { kind: "labelTail"; p: Vec }
  | { kind: "dimA"; p: Vec } // dimension: first measured point (drag to move it; snaps re-attach it)
  | { kind: "dimB"; p: Vec } // dimension: second measured point (radius/diameter: the leader end)
  | { kind: "dimLine"; p: Vec } // dimension: the dimension line (drag to move it away from or toward the points)
  | { kind: "gridCorner"; i: number; p: Vec } // ANN-6 resize per lockAspect
  | { kind: "pipeEnd"; end: "a" | "b"; p: Vec } // pipe: drag an end; drop on a free end to attach
  | { kind: "wallVertex"; i: number; p: Vec }; // 3.23.0: an end or a corner of a wall run

type DragState =
  | { mode: "idle" }
  | { mode: "maybeDrag"; start: Vec; startScreen: Vec; entityId: string }
  | { mode: "move"; start: Vec; delta: Vec; align?: AlignInput; guides?: AlignGuide[]; scale?: number }
  | { mode: "marquee"; start: Vec; startScreen: Vec; current: Vec }
  | { mode: "grip"; grip: Grip; entityId: string; original: Entity; preview: Entity }
  // selection transform frame (works for every entity type, single or multi)
  | { mode: "rotate"; pivot: Vec; startCursorDeg: number; deg: number }
  | { mode: "scale"; anchor: Vec; startDist: number; factor: number }
  // drag the turning point of one object (owner, 2026-10-06)
  | { mode: "pivot"; entityId: string; p: Vec }
  // drag one end of a pipe (owner, 2026-10-06): `fixed` is the other end, `port` the end it would attach to
  | { mode: "pipeEnd"; entityId: string; end: "a" | "b"; fixed: Vec; p: Vec; size: number; port: Port | null; moved: boolean }
  // 3.21.0: a door picked in its wall; a drag slides it along the wall. `grab` = how far along the wall the press was
  // from the centre of the door; `at` = the centre the door gets at the drop; `p` = the pointer (for the readout)
  | { mode: "door"; wallId: string; doorId: string; grab: number; at0: number; at: number; startScreen: Vec; p: Vec; moved: boolean }
  // 3.23.0: an end or a corner of a wall run dragged: `to` the spine points it gives, `on` what it joins there
  | { mode: "wallVertex"; wallId: string; k: number; from: Vec[]; to: Vec[]; closed: boolean; p: Vec; on: "end" | "wall" | null; moved: boolean }
  // TL-5a: hold E (perpendicular slide) / W (free move) / X (extrude: original corners stay,
  // two new points are inserted — one segment becomes three) and drag a polyline edge.
  | {
      mode: "edge";
      kind: "perp" | "free" | "extrude";
      entityId: string;
      segIndex: number;
      start: Vec;
      normal: Vec; // unit normal of the grabbed segment (perp/extrude modes)
      offset: number; // signed perpendicular distance so far (readout/typed entry)
      original: PolylineEntity;
      preview: PolylineEntity;
    };

/**
 * 3.20.0: `ids` moved by `d` and scaled by `k` about the grab point, in ONE uniform scale: the grab point lands at
 * grab + d, and every point p goes to grab + d + k (p - grab). That is a scale by k about Q = (grab + d - k grab) / (1 - k).
 */
export function movedAndScaled(doc: Doc, ids: Iterable<string>, grab: Vec, d: Vec, k: number) {
  const a = { x: grab.x + d.x, y: grab.y + d.y };
  const q = { x: (a.x - k * grab.x) / (1 - k), y: (a.y - k * grab.y) / (1 - k) };
  return scalePatches(doc, ids, q, k);
}

export class SelectTool implements Tool {
  readonly id = "select" as const;
  private state: DragState = { mode: "idle" };
  private hoverId: string | null = null;
  /** Group edit-in-place: the group(s) being edited, innermost last (TL-5). */
  private editStack: string[] = [];

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.state = { mode: "idle" };
    this.editStack = [];
    this.ctx.setMeasure("none", "");
    this.ctx.setSnapBase(null);
    this.syncHint();
  }

  private editingGroup(): string | null {
    return this.editStack[this.editStack.length - 1] ?? null;
  }

  private isGroupMember(id: string): boolean {
    const g = this.editingGroup();
    if (!g) return false;
    const group = this.ctx.store.doc.entities[g];
    return group?.type === "group" && group.children.includes(id);
  }

  private enterGroup(id: string): void {
    this.editStack.push(id);
    this.ctx.selection.clear();
    this.ctx.setHint("Editing group: click parts to edit them · Esc or double-click outside = done");
    this.ctx.requestRender();
  }

  private exitGroup(): void {
    const left = this.editStack.pop();
    if (left) this.ctx.selection.set([left]); // hand the group itself back on exit
    this.syncHint();
    this.ctx.requestRender();
  }

  deactivate(): void {
    this.cancel();
  }

  cancel(): void {
    this.state = { mode: "idle" };
    this.ctx.setEditing([]);
    this.ctx.gestureActive(false);
    this.ctx.setMeasure("none", "");
    this.ctx.setCursor(null);
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
    this.syncHint();
  }

  inProgress(): boolean {
    return this.state.mode !== "idle";
  }

  /** 3.21.0: the wall drawn live while one of its doors is dragged (its selection outline would show the old door). */
  drawnLive(): string | null {
    if (this.state.mode === "wallVertex" && this.state.moved) return this.state.wallId;
    return this.state.mode === "door" && this.state.moved ? this.state.wallId : null;
  }

  private syncHint(): void {
    const n = this.ctx.selection.size;
    if (n === 0) {
      this.ctx.setHint(
        "Select: click or drag a box · right-click = menu · hold E/W/X + drag a wall: slide ⟂ / move / pull out new"
      );
    } else if (n === 1) {
      this.ctx.setHint(
        "Select: drag = move · Ctrl at drop = copy · E/W/X + drag wall · right-click = more · Del = delete"
      );
    } else {
      this.ctx.setHint(
        `Select: ${n} selected · drag = move · Ctrl+G = group · right-click = more · Del = delete`
      );
    }
  }

  private tol(): number {
    return HIT_TOL_PX / this.ctx.scale();
  }

  private hitCtx() {
    return { doc: this.ctx.store.doc, index: this.ctx.index, insideGroup: this.editingGroup() ?? undefined };
  }

  /** TL-5 move snapping: up to 8 reference points of the dragged selection. */
  /** What the alignment needs, gathered once at the start of a drag (nothing in it moves while the drag runs). */
  private alignInput(): AlignInput | undefined {
    const doc = this.ctx.store.doc;
    const sel = new Set(this.ctx.selection.all);
    if (sel.size === 0) return undefined;
    const owners = [...sel].filter((id) => looksLikeFitting(doc.entities[id]));
    // 3.24.0: a vertical end (riser, drop, top of a pump) has no axis on the plan: it is not an align end
    const movingPorts: Port[] = owners.flatMap((id) => safePorts(doc, id)).filter((p) => !p.vertical);
    // pipes on a moving component follow it: they are not targets
    const follow = new Set<string>();
    for (const e of Object.values(doc.entities)) {
      const pm = looksLikePipe(e) ? pipeMetaOf(e) : null;
      if (pm && [pm.a, pm.b].some((a) => a.kind === "port" && sel.has(a.ownerId))) follow.add(e.id);
    }
    const visible = new Set(doc.layers.filter((l) => l.visible).map((l) => l.id));
    let targetPorts: Port[] = [];
    try {
      targetPorts = allPorts(doc).filter((p) => !p.vertical && !sel.has(p.ownerId) && !follow.has(p.ownerId) && visible.has(doc.entities[p.ownerId]?.layerId ?? ""));
    } catch {
      targetPorts = [];
    }
    const box = (id: string): Box | null => {
      const ent = doc.entities[id];
      if (!ent) return null;
      const b = entityBounds(ent, doc);
      return b.minX <= b.maxX ? b : null;
    };
    let movingBox: Box | null = null;
    for (const id of sel) {
      const b = box(id);
      if (!b) continue;
      movingBox = movingBox
        ? { minX: Math.min(movingBox.minX, b.minX), minY: Math.min(movingBox.minY, b.minY), maxX: Math.max(movingBox.maxX, b.maxX), maxY: Math.max(movingBox.maxY, b.maxY) }
        : { ...b };
    }
    const SKIP = new Set(["image", "grid", "dimension", "label"]);
    const targetBoxes: Box[] = [];
    for (const layer of doc.layers) {
      if (!layer.visible) continue;
      for (const id of doc.zOrder[layer.id] ?? []) {
        if (sel.has(id) || follow.has(id)) continue;
        const ent = doc.entities[id];
        if (!ent || ent.hidden || SKIP.has(ent.type)) continue;
        const b = box(id);
        if (b) targetBoxes.push(b);
        if (targetBoxes.length >= 3000) break;
      }
    }
    return { movingPorts, movingBox, targetPorts, targetBoxes, nameOf: (id) => ownerName(doc, id) };
  }

  private moveRefPoints(): Vec[] {
    const doc = this.ctx.store.doc;
    const out: Vec[] = [];
    for (const id of this.ctx.selection.all) {
      const e = doc.entities[id];
      if (!e) continue;
      switch (e.type) {
        case "polyline":
        case "marker":
          out.push(...e.points.slice(0, 8 - out.length));
          break;
        case "circle":
        case "arc":
          out.push(e.center);
          break;
        case "label":
          out.push(e.head, e.tail);
          break;
        case "text":
        case "image":
        case "grid":
          out.push(e.pos);
          break;
        default:
          break;
      }
      if (out.length >= 8) break;
    }
    return out.slice(0, 8);
  }

  // ---- grips -------------------------------------------------------------

  private grips(): { entityId: string; grips: Grip[] } | null {
    if (this.ctx.selection.size !== 1) return null; // multi-select: bbox only (TL-5)
    const id = [...this.ctx.selection.all][0]!;
    const doc = this.ctx.store.doc;
    const entity = doc.entities[id];
    if (!entity || isLocked(doc, id)) return null;
    const grips: Grip[] = [];
    switch (entity.type) {
      case "polyline": {
        entity.points.forEach((p, i) => grips.push({ kind: "vertex", i, p }));
        const segCount = entity.points.length - 1 + (entity.closed && entity.points.length > 2 ? 1 : 0);
        for (let i = 0; i < segCount; i++) {
          const a = entity.points[i]!;
          const b = entity.points[(i + 1) % entity.points.length]!;
          grips.push({ kind: "midInsert", i, p: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } });
        }
        break;
      }
      case "circle":
        grips.push({ kind: "center", p: entity.center });
        grips.push({ kind: "radius", p: { x: entity.center.x + entity.r, y: entity.center.y } });
        break;
      case "arc":
        grips.push({ kind: "center", p: entity.center });
        grips.push({ kind: "arcStart", p: arcPoint(entity.center, entity.r, entity.startDeg) });
        grips.push({ kind: "arcEnd", p: arcPoint(entity.center, entity.r, entity.startDeg + entity.sweepDeg) });
        grips.push({
          kind: "arcBulge",
          p: arcPoint(entity.center, entity.r, entity.startDeg + entity.sweepDeg / 2)
        });
        break;
      case "image": {
        const corners = imageCorners(entity);
        corners.forEach((p, i) => grips.push({ kind: "imgCorner", i, p }));
        // rotate grip 24 px above the top edge midpoint (TL-5)
        const topMid = { x: (corners[0]!.x + corners[1]!.x) / 2, y: (corners[0]!.y + corners[1]!.y) / 2 };
        const up = dir(entity.rotationDeg - 90);
        const off = 24 / this.ctx.scale();
        grips.push({ kind: "imgRotate", p: { x: topMid.x + up.x * off, y: topMid.y + up.y * off } });
        break;
      }
      case "label":
        grips.push({ kind: "labelHead", p: entity.head });
        grips.push({ kind: "labelTail", p: entity.tail });
        break;
      case "dimension": {
        const g = dimensionGeometry(doc, entity);
        if (!g) break;
        if (entity.kind === "linear") {
          grips.push({ kind: "dimA", p: g.a }, { kind: "dimB", p: g.b }, { kind: "dimLine", p: g.textPos });
        } else {
          if (entity.a.kind === "free") grips.push({ kind: "dimA", p: g.a });
          grips.push({ kind: "dimB", p: g.b });
        }
        break;
      }
      case "grid": {
        const w = entity.cols * entity.cellW;
        const h = entity.rows * entity.cellH;
        const raw: Vec[] = [
          entity.pos,
          { x: entity.pos.x + w, y: entity.pos.y },
          { x: entity.pos.x + w, y: entity.pos.y + h },
          { x: entity.pos.x, y: entity.pos.y + h }
        ];
        raw.forEach((p, i) => grips.push({ kind: "gridCorner", i, p })); // rotation ≈ 0 in practice
        break;
      }
      case "group": {
        // 3.23.0: a wall run: a grip on each end and each corner of its spine (lengthen, shorten, move a corner)
        const spine = wallMetaOf(entity) ? spineOf(doc, id) : null;
        if (spine) {
          spine.points.forEach((p, i) => grips.push({ kind: "wallVertex", i, p }));
          break;
        }
        // a pipe: one grip on each end of its centre line
        if (!looksLikePipe(entity)) break;
        const ends = pipeEnds(doc, id);
        if (ends) grips.push({ kind: "pipeEnd", end: "a", p: ends.a }, { kind: "pipeEnd", end: "b", p: ends.b });
        break;
      }
      default:
        break;
    }
    return { entityId: id, grips };
  }

  private gripAtWorld(world: Vec): { entityId: string; grip: Grip } | null {
    const g = this.grips();
    if (!g) return null;
    const tol = GRIP_PX / this.ctx.scale();
    let best: { entityId: string; grip: Grip; d: number } | null = null;
    for (const grip of g.grips) {
      const d = dist(grip.p, world);
      if (d <= tol && (!best || d < best.d)) best = { entityId: g.entityId, grip, d };
    }
    return best;
  }

  // ---- transform frame (rotate + scale, all entity types) -----------------

  /** Padded bbox of the selection with corner scale handles + a rotate knob. */
  private transformFrame(): { corners: Vec[]; rotP: Vec; center: Vec; pivot: Vec; ownPivot: boolean; b: Bounds } | null {
    if (this.ctx.selection.size === 0) return null;
    const doc = this.ctx.store.doc;
    // single images already carry their own corner-scale + rotate grips — no double frame
    if (this.ctx.selection.size === 1) {
      const only = doc.entities[[...this.ctx.selection.all][0]!];
      // a label: its text and its two end grips; a frame round the leader covered the parts under it
      if (only?.type === "image" || only?.type === "dimension" || only?.type === "label") return null;
      // a pipe is shaped by its two end grips; scale/rotate handles would fight them
      if (looksLikePipe(only)) return null;
    }
    const b: Bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    for (const id of this.ctx.selection.all) {
      const entity = doc.entities[id];
      if (!entity) continue;
      const eb = entityBounds(entity, doc);
      if (eb.minX > eb.maxX) continue;
      b.minX = Math.min(b.minX, eb.minX);
      b.minY = Math.min(b.minY, eb.minY);
      b.maxX = Math.max(b.maxX, eb.maxX);
      b.maxY = Math.max(b.maxY, eb.maxY);
    }
    if (b.minX > b.maxX) return null;
    const pad = 12 / this.ctx.scale();
    const corners: Vec[] = [
      { x: b.minX - pad, y: b.minY - pad },
      { x: b.maxX + pad, y: b.minY - pad },
      { x: b.maxX + pad, y: b.maxY + pad },
      { x: b.minX - pad, y: b.maxY + pad }
    ];
    const rotP = { x: (b.minX + b.maxX) / 2, y: b.minY - pad - 24 / this.ctx.scale() };
    const center = { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
    // one object with a set turning point turns about it; anything else about the centre
    const own = this.selectedOwnPivot();
    return { corners, rotP, center, pivot: own ?? center, ownPivot: own !== null, b };
  }

  private transformHandleAtWorld(world: Vec): { kind: "rotate" } | { kind: "scale"; i: number } | null {
    const f = this.transformFrame();
    if (!f) return null;
    const tol = GRIP_PX / this.ctx.scale();
    if (dist(world, f.rotP) <= tol) return { kind: "rotate" };
    for (let i = 0; i < 4; i++) if (dist(world, f.corners[i]!) <= tol) return { kind: "scale", i };
    return null;
  }

  /** The set turning point of the one selected object, or null. */
  private selectedOwnPivot(): Vec | null {
    if (this.ctx.selection.size !== 1) return null;
    return pivotOf(this.ctx.store.doc.entities[[...this.ctx.selection.all][0]!]);
  }

  /** The one selected object whose turning-point marker is under the cursor, or null. */
  private pivotHandleAt(world: Vec): { id: string; p: Vec } | null {
    if (this.ctx.selection.size !== 1) return null;
    const id = [...this.ctx.selection.all][0]!;
    if (isLocked(this.ctx.store.doc, id)) return null;
    const f = this.transformFrame();
    if (!f) return null;
    return dist(world, f.pivot) <= GRIP_PX / this.ctx.scale() ? { id, p: f.pivot } : null;
  }

  private unlockedSelection(): string[] {
    const doc = this.ctx.store.doc;
    return [...this.ctx.selection.all].filter((id) => !isLocked(doc, id));
  }

  private tryStartTransform(e: ToolPointerEvent): boolean {
    const handle = this.transformHandleAtWorld(e.world);
    if (!handle) return false;
    if (this.unlockedSelection().length === 0) {
      this.ctx.setHint("Locked — unlock via right-click or the Layers panel (EC-TL-15)");
      return true;
    }
    if (handle.kind !== "rotate" && [...this.ctx.selection.all].some((id) => isComponent(this.ctx.store.doc.entities[id]))) {
      this.ctx.setHint("A component keeps its size: a scaled fitting, pump or lint pot no longer matches its ends. Change its size in the Inspector.");
      return true;
    }
    const f = this.transformFrame()!;
    this.ctx.setEditing(expandGroupChildren(this.ctx.store.doc, this.ctx.selection.all));
    this.ctx.gestureActive(true);
    this.ctx.setSnapBase(null);
    this.ctx.setCursor(handle.kind === "rotate" ? "grabbing" : "nwse-resize");
    if (handle.kind === "rotate") {
      this.state = { mode: "rotate", pivot: f.pivot, startCursorDeg: angleOf(sub(e.world, f.pivot)), deg: 0 };
      this.ctx.setMeasure("length", "angle (degrees)");
      this.ctx.setHint("Rotate: drag around the selection — type an exact angle ⏎ · Shift = 15° steps · Esc = cancel");
      this.ctx.setCursorTip("drag to rotate · angle ⏎");
    } else {
      const anchor = f.corners[(handle.i + 2) % 4]!;
      const d0 = Math.max(dist(e.world, anchor), 1e-6);
      this.state = { mode: "scale", anchor, startDist: d0, factor: 1 };
      this.ctx.setMeasure("length", "scale factor (e.g. 1.5)");
      this.ctx.setHint("Scale: drag toward/away from the opposite corner — type an exact factor ⏎ · Esc = cancel");
      this.ctx.setCursorTip("drag to scale · factor ⏎");
    }
    this.ctx.requestRender();
    return true;
  }

  private commitTransform(): void {
    const st = this.state;
    const doc = this.ctx.store.doc;
    const ids = this.unlockedSelection();
    this.ctx.setEditing([]);
    this.ctx.gestureActive(false);
    this.ctx.setMeasure("none", "");
    this.ctx.setCursorTip(null);
    this.ctx.setCursor(null);
    this.state = { mode: "idle" };
    if (st.mode === "rotate" && Math.abs(st.deg) > 1e-9) {
      const patches = rotatePatches(doc, ids, st.pivot, st.deg);
      if (patches.length) this.ctx.store.dispatch({ kind: "updateEntities", patches });
    } else if (st.mode === "scale" && Math.abs(st.factor - 1) > 1e-9 && st.factor > 0) {
      const patches = scalePatches(doc, ids, st.anchor, st.factor);
      if (patches.length) this.ctx.store.dispatch({ kind: "updateEntities", patches });
    }
    this.syncHint();
    this.ctx.requestRender();
  }

  // ---- pointer -----------------------------------------------------------

  pointerDown(e: ToolPointerEvent): void {
    if (e.button !== 0) return;
    const doc = this.ctx.store.doc;

    if (e.alt && this.tryVertexDelete(e)) return;

    // TL-5a: E/W/X held → edge slide/move/extrude takes precedence over grips and body-move
    if ((e.held.has("e") || e.held.has("w") || e.held.has("x")) && this.tryStartEdgeDrag(e)) return;

    // transform frame handles (rotate knob / scale corners) — outside the geometry
    if (!e.shift && this.tryStartTransform(e)) return;

    // Shift = additive selection mode; grips don't intercept (TL-5)
    const grip = e.shift ? null : this.gripAtWorld(e.world);
    if (grip && grip.grip.kind === "pipeEnd") {
      const ends = pipeEnds(doc, grip.entityId);
      const meta = pipeMetaOf(doc.entities[grip.entityId]);
      if (!ends || !meta) return;
      const end = grip.grip.end;
      this.state = {
        mode: "pipeEnd",
        entityId: grip.entityId,
        end,
        fixed: end === "a" ? ends.b : ends.a,
        p: grip.grip.p,
        size: meta.size,
        port: null,
        moved: false
      };
      // the pipe's own lines do not attract the cursor while its end moves
      this.ctx.setEditing(expandGroupChildren(doc, [grip.entityId]));
      this.ctx.gestureActive(true);
      this.ctx.setSnapBase(this.state.fixed); // Shift keeps the pipe level or plumb
      this.ctx.setCursor("grabbing");
      this.ctx.setHint("Pipe end: drag it — drop it on a free fitting or pipe end to attach · Shift = lock axis · Esc = cancel");
      this.ctx.requestRender();
      return;
    }
    if (grip && grip.grip.kind === "wallVertex") {
      const spine = spineOf(doc, grip.entityId);
      if (!spine) return;
      this.ctx.selectDoor(null);
      const k = grip.grip.i;
      this.state = { mode: "wallVertex", wallId: grip.entityId, k, from: spine.points.map((q) => ({ ...q })), to: spine.points.map((q) => ({ ...q })), closed: spine.closed, p: e.world, on: null, moved: false };
      this.ctx.setEditing(expandGroupChildren(doc, [grip.entityId]));
      this.ctx.gestureActive(true);
      this.ctx.setSnapBase(null);
      this.ctx.setCursor("grabbing");
      const isEnd = !spine.closed && (k === 0 || k === spine.points.length - 1);
      this.ctx.setHint(
        isEnd
          ? "Wall end: drag along the wall to make it longer or shorter · onto another wall to join it · Ctrl = move it freely · Esc = cancel"
          : "Wall corner: drag it · a rectangular room stays rectangular · Ctrl = move this corner alone · Esc = cancel"
      );
      this.ctx.requestRender();
      return;
    }
    if (grip) {
      const entity = doc.entities[grip.entityId]!;
      const preview = structuredClone(entity) as Entity;
      this.state = { mode: "grip", grip: grip.grip, entityId: grip.entityId, original: entity, preview };
      this.ctx.setEditing([grip.entityId]);
      this.ctx.gestureActive(true);
      this.ctx.setSnapBase(grip.grip.p);
      this.ctx.setCursor("grabbing");
      this.ctx.requestRender();
      return;
    }

    // the turning-point marker: drag it to the point to turn about
    const pv = e.shift ? null : this.pivotHandleAt(e.world);
    if (pv) {
      this.state = { mode: "pivot", entityId: pv.id, p: pv.p };
      this.ctx.gestureActive(true);
      this.ctx.setSnapBase(null);
      this.ctx.setCursor("grabbing");
      this.ctx.setHint("Turning point: drag it to the point to turn about · snaps work · Esc = cancel");
      this.ctx.requestRender();
      return;
    }

    // 3.21.0: a door in a wall. A click picks it (the Inspector shows it); a drag slides it along its wall.
    // Owner, 2026-10-21: "you can actually adjust the location of the door by sliding it".
    const door = e.shift ? null : this.doorUnder(e.world);
    if (door) {
      this.ctx.selectDoor({ wallId: door.wallId, doorId: door.doorId });
      if (isLocked(doc, door.wallId)) {
        this.ctx.setHint("Locked — unlock via right-click or the Layers panel (EC-TL-15)");
        this.state = { mode: "idle" };
        this.ctx.requestRender();
        return;
      }
      this.state = { mode: "door", wallId: door.wallId, doorId: door.doorId, grab: door.u - door.uc, at0: door.uc, at: door.uc, startScreen: e.screen, p: e.world, moved: false };
      this.ctx.gestureActive(true);
      this.ctx.setSnapBase(null);
      this.ctx.setHint("Door: drag it along the wall · its settings are in the Inspector · Delete = take it away · Esc = drop the pick");
      this.ctx.requestRender();
      return;
    }

    const hit = hitTest(this.hitCtx(), e.world, this.tol());
    if (hit) {
      if (e.shift) {
        this.ctx.selection.toggle(hit);
        this.syncHint();
        this.ctx.requestRender();
        return;
      }
      if (!this.ctx.selection.has(hit)) this.ctx.selection.set([hit]);
      this.ctx.selectDoor(null); // the wall itself, not its door
      // locked entities select fine but never start a drag — say so immediately (EC-TL-15)
      if (isLocked(doc, hit)) {
        this.ctx.setHint("Locked — unlock via right-click or the Layers panel (EC-TL-15)");
        this.state = { mode: "idle" };
        this.ctx.requestRender();
        return;
      }
      this.state = { mode: "maybeDrag", start: e.world, startScreen: e.screen, entityId: hit };
      this.syncHint();
      this.ctx.requestRender();
      return;
    }

    if (!e.shift) this.ctx.selection.clear();
    this.state = { mode: "marquee", start: e.world, startScreen: e.screen, current: e.world };
    this.ctx.gestureActive(true);
    this.syncHint();
    this.ctx.requestRender();
  }

  /**
   * The door under `world`: in the one selected wall (its opening or its swing, also the empty space of the swing),
   * or in the wall the pointer is on. With how far along its wall the point is (`u`) and the door's drawn centre (`uc`).
   */
  private doorUnder(world: Vec): { wallId: string; doorId: string; u: number; uc: number } | null {
    const doc = this.ctx.store.doc;
    const sel = [...this.ctx.selection.all];
    const walls: string[] = [];
    if (sel.length === 1 && wallMetaOf(doc.entities[sel[0]!])) walls.push(sel[0]!);
    const hit = hitTest(this.hitCtx(), world, this.tol());
    if (hit && !walls.includes(hit) && wallMetaOf(doc.entities[hit])) walls.push(hit);
    // the swing of a door is empty space, so nothing is hit there: every visible wall is asked too (topmost first)
    if (!this.editingGroup()) {
      for (const layer of [...doc.layers].reverse()) {
        if (!layer.visible) continue;
        for (const id of [...(doc.zOrder[layer.id] ?? [])].reverse()) {
          const e = doc.entities[id];
          if (e && !e.hidden && !walls.includes(id) && wallMetaOf(e)) walls.push(id);
        }
      }
    }
    for (const id of walls) {
      const d = doorAt(doc, id, world);
      const w = wallMetaOf(doc.entities[id]);
      const spine = spineOf(doc, id);
      const geo = d && w && spine ? wallGeometry(spine.points, spine.closed, w) : null;
      const span = geo && d ? doorSpan(geo, d) : null;
      if (!d || !span) continue;
      const s = span.seg;
      return { wallId: id, doorId: d.id, u: (world.x - s.a.x) * s.d.x + (world.y - s.a.y) * s.d.y, uc: span.uc };
    }
    return null;
  }

  /** TL-5a: find a polyline edge under the cursor and begin a slide (perp) or move (free) drag. */
  private tryStartEdgeDrag(e: ToolPointerEvent): boolean {
    const doc = this.ctx.store.doc;
    const hit = hitTest(this.hitCtx(), e.world, this.tol());
    const entity = hit ? doc.entities[hit] : undefined;
    if (!entity || entity.type !== "polyline") return false;
    if (isLocked(doc, entity.id)) {
      this.ctx.setHint("Locked — unlock via right-click or the Layers panel (EC-TL-15)");
      return true; // consumed: don't fall through to body-move
    }
    const seg = nearestSegment(entity, e.world, this.tol());
    if (!seg) return false;
    const kind = e.held.has("e") ? "perp" : e.held.has("x") ? "extrude" : "free";
    this.ctx.selection.set([entity.id]);
    this.state = {
      mode: "edge",
      kind,
      entityId: entity.id,
      segIndex: seg.index,
      start: e.point,
      normal: seg.normal,
      offset: 0,
      original: entity,
      preview: structuredClone(entity)
    };
    this.ctx.setEditing([entity.id]);
    this.ctx.gestureActive(true);
    this.ctx.setSnapBase(e.point);
    this.ctx.setCursor("move");
    const typed = kind !== "free";
    this.ctx.setMeasure(typed ? "length" : "none", typed ? "distance" : "");
    this.ctx.setHint(
      kind === "perp"
        ? "Slide wall: drag in/out (perpendicular) — type a distance ⏎ · Esc = cancel"
        : kind === "extrude"
          ? "Pull out new wall: corners stay, new points added — type a distance ⏎ · Esc = cancel"
          : "Move wall: drag freely · Esc = cancel"
    );
    this.ctx.requestRender();
    return true;
  }

  private applyEdgeDelta(s: Extract<DragState, { mode: "edge" }>, delta: Vec): void {
    const pts = s.original.points;
    const i0 = s.segIndex;
    const i1 = (s.segIndex + 1) % pts.length;
    if (s.kind === "extrude") {
      // original corners stay; insert a', b' offset along the normal → 1 segment becomes 3
      const k = delta.x * s.normal.x + delta.y * s.normal.y;
      s.offset = k;
      if (Math.abs(k) < 1e-9) {
        s.preview.points = pts.map((p) => ({ ...p })); // no dup points at zero offset (INV-4)
        return;
      }
      const a = pts[i0]!;
      const b = pts[i1]!;
      const aOut = { x: a.x + s.normal.x * k, y: a.y + s.normal.y * k };
      const bOut = { x: b.x + s.normal.x * k, y: b.y + s.normal.y * k };
      const next = pts.map((p) => ({ ...p }));
      next.splice(i0 + 1, 0, aOut, bOut); // works for the closing segment too (appends before wrap)
      s.preview.points = next;
      return;
    }
    let d = delta;
    if (s.kind === "perp") {
      const k = delta.x * s.normal.x + delta.y * s.normal.y;
      s.offset = k;
      d = { x: s.normal.x * k, y: s.normal.y * k };
    }
    s.preview.points = pts.map((p, i) => (i === i0 || i === i1 ? { x: p.x + d.x, y: p.y + d.y } : p));
  }

  private commitEdgeDrag(s: Extract<DragState, { mode: "edge" }>): void {
    this.ctx.setEditing([]);
    this.ctx.gestureActive(false);
    this.ctx.setSnapBase(null);
    this.ctx.setMeasure("none", "");
    this.state = { mode: "idle" };
    // zero-offset extrude would insert duplicate points — treat as cancel (INV-4)
    const noop = s.kind === "extrude" && Math.abs(s.offset) < 1e-9;
    const patch = noop ? null : diffEntity(s.original, s.preview);
    if (patch) this.ctx.store.dispatch({ kind: "updateEntities", patches: [patch] });
    this.syncHint();
    this.ctx.requestRender();
  }

  pointerMove(e: ToolPointerEvent): void {
    const doc = this.ctx.store.doc;
    switch (this.state.mode) {
      case "idle": {
        const handle = this.transformHandleAtWorld(e.world);
        const grip = handle ? null : this.gripAtWorld(e.world);
        const onPivot = handle || grip ? null : this.pivotHandleAt(e.world);
        const hit = grip || handle || onPivot ? null : hitTest(this.hitCtx(), e.world, this.tol());
        if (hit !== this.hoverId) {
          this.hoverId = hit;
          this.ctx.requestRender();
        }
        const onDoor = grip || handle || onPivot ? null : this.doorUnder(e.world);
        // the cursor tells you what a press would do (UI polish: hover affordance)
        this.ctx.setCursor(
          onDoor
            ? isLocked(doc, onDoor.wallId)
              ? "not-allowed"
              : "grab"
            : handle
            ? handle.kind === "rotate"
              ? "grab"
              : "nwse-resize"
            : grip
              ? gripHoverCursor(grip.grip.kind)
              : onPivot
                ? "grab"
                : hit
                ? isLocked(doc, hit)
                  ? "not-allowed"
                  : "move"
                : null
        );
        break;
      }
      case "wallVertex": {
        const s = this.state;
        s.p = e.world;
        s.moved = true;
        // onto another wall: its free end (a mitre) or its centre line (a T). A sliding end meets that centre line.
        const tol = 12 / this.ctx.scale();
        const n = s.from.length;
        const isEnd = !s.closed && (s.k === 0 || s.k === n - 1);
        // the other end of this run (a room broken at a corner closes again there), else another wall
        const other = isEnd ? s.from[s.k === 0 ? n - 1 : 0]! : null;
        const at = other && n > 2 && Math.hypot(e.world.x - other.x, e.world.y - other.y) <= tol ? { p: { ...other }, kind: "end" as const, wallId: s.wallId } : wallAttachPoint(doc, e.world, tol, new Set([s.wallId]));
        let target = at?.p ?? e.point;
        if (at?.kind === "wall" && at.along && isEnd && !e.ctrl) {
          const adj = s.from[s.k === 0 ? 1 : n - 2]!;
          const d = { x: s.from[s.k]!.x - adj.x, y: s.from[s.k]!.y - adj.y };
          const den = d.x * at.along.y - d.y * at.along.x;
          if (Math.abs(den) > 1e-9) {
            const q = { x: at.p.x - adj.x, y: at.p.y - adj.y };
            const tt = (q.x * at.along.y - q.y * at.along.x) / den;
            target = { x: adj.x + d.x * tt, y: adj.y + d.y * tt };
          }
        }
        const pts = movedWallPoints(s.from, s.closed, s.k, target, e.ctrl || at?.kind === "end");
        if (pts) s.to = pts;
        s.on = at ? at.kind : null;
        this.ctx.requestRender();
        return;
      }
      case "door": {
        const s = this.state;
        s.p = e.world;
        if (!s.moved && dist(e.screen, s.startScreen) < 4) return;
        if (!s.moved) this.ctx.setEditing(expandGroupChildren(doc, [s.wallId])); // the wall is drawn live (renderDynamic)
        s.moved = true;
        this.ctx.setCursor("grabbing");
        const at = slideDoorAt(doc, s.wallId, s.doorId, e.world, s.grab);
        if (at !== null) s.at = at;
        this.ctx.requestRender();
        return;
      }
      case "maybeDrag": {
        if (dist(e.screen, this.state.startScreen) < 4) return;
        if ([...this.ctx.selection.all].some((id) => isLocked(doc, id))) {
          this.ctx.setHint("Locked — unlock via right-click or the Layers panel (EC-TL-15)");
          this.state = { mode: "idle" };
          return;
        }
        const start = this.state.start;
        this.state = { mode: "move", start, delta: sub(e.point, start), align: this.alignInput() };
        // expand group children so the cursor doesn't snap against the geometry being moved
        this.ctx.setEditing(expandGroupChildren(doc, this.ctx.selection.all));
        this.ctx.gestureActive(true);
        this.ctx.setSnapBase(start);
        this.ctx.setCursor("move");
        this.ctx.requestRender();
        break;
      }
      case "move": {
        this.state.delta = sub(e.point, this.state.start);
        // Only a snap to a real point (end, crossing, middle, centre, quadrant) beats the alignment below. A tracking
        // line from the start point ("ray"), the nearest point on an edge, a guide or the grid do not.
        let pointSnap: Vec | null = POINT_SNAPS.has(e.snap.kind) ? this.state.delta : null;
        // TL-5: the entity's OWN endpoints snap too — when the cursor found nothing
        // meaningful, translate each reference point and take the strongest snag
        if (e.snap.kind === "free" || e.snap.kind === "grid" || e.snap.kind === "ortho") {
          const raw = sub(e.world, this.state.start);
          let best: { d: number; delta: Vec; kind: string } | null = null;
          for (const p of this.moveRefPoints()) {
            const target = { x: p.x + raw.x, y: p.y + raw.y };
            const s = this.ctx.resolveSnap(target);
            if (s.kind === "free" || s.kind === "grid" || s.kind === "guide" || s.kind === "ray") continue;
            const d = dist(s.point, target);
            if (!best || d < best.d) best = { d, delta: sub(s.point, p), kind: s.kind };
          }
          if (best) {
            this.state.delta = best.delta;
            if (POINT_SNAPS.has(best.kind)) pointSnap = best.delta;
          }
        }
        // 3.14.2: ends on ends, ends on centre lines, edges on edges (alignSnap.ts). Ortho (Shift) keeps its line.
        let alignHint: string | null = null;
        this.state.guides = [];
        if (this.state.align && e.snap.kind !== "ortho") {
          const r = alignMove(this.state.align, sub(e.world, this.state.start), pointSnap, ALIGN_PX / this.ctx.scale());
          if (r.guides.length > 0) {
            this.state.delta = r.delta;
            this.state.guides = r.guides;
            alignHint = r.hint;
          }
        }
        this.ctx.setHint(alignHint ?? "Move: release to place · hold Ctrl to copy · Esc = cancel");
        this.ctx.requestRender();
        break;
      }
      case "marquee": {
        this.state.current = e.world;
        const window = e.screen.x >= this.state.startScreen.x;
        this.ctx.setHint(window ? "Window select: fully inside" : "Crossing select: touching");
        this.ctx.requestRender();
        break;
      }
      case "grip": {
        this.applyGrip(this.state, e.point, e.shift, e.snap);
        this.ctx.requestRender();
        break;
      }
      case "edge": {
        const s = this.state;
        this.applyEdgeDelta(s, sub(e.point, s.start));
        if (s.kind !== "free") {
          const { units, precision } = this.ctx.docUnits();
          const verb = s.kind === "perp" ? "Slide wall" : "Pull out";
          this.ctx.setHint(
            `${verb}: ${formatLength(Math.abs(s.offset), units, precision)} — type an exact distance ⏎`
          );
        }
        this.ctx.requestRender();
        break;
      }
      case "rotate": {
        const s = this.state;
        let deg = angleOf(sub(e.world, s.pivot)) - s.startCursorDeg;
        deg = ((deg + 540) % 360) - 180; // signed, shortest way
        if (e.shift) deg = Math.round(deg / 15) * 15;
        s.deg = deg;
        this.ctx.setHint(`Rotate: ${deg.toFixed(1)}° — type an exact angle ⏎ · Shift = 15° steps`);
        this.ctx.requestRender();
        break;
      }
      case "pivot": {
        this.state.p = e.point;
        const { units, precision } = this.ctx.docUnits();
        this.ctx.setHint(
          `Turning point: X ${formatLength(e.point.x, units, precision)}, Y ${formatLength(e.point.y, units, precision)} · release to set`
        );
        this.ctx.requestRender();
        break;
      }
      case "pipeEnd": {
        const s = this.state;
        s.moved = true;
        s.p = e.point;
        s.port = this.pipeTargetPort(s, e.point);
        if (s.port) s.p = s.port.p;
        const { units, precision } = this.ctx.docUnits();
        this.ctx.setHint(
          s.port
            ? `Pipe end: release to attach to the ${s.port.label} (${s.port.size} mm)`
            : `Pipe end: ${formatLength(dist(s.fixed, s.p), units, precision)} long · release to leave it free here`
        );
        this.ctx.setCursorTip(s.port ? `attach · ${s.port.label}` : null);
        this.ctx.requestRender();
        break;
      }
      case "scale": {
        const s = this.state;
        s.factor = Math.max(dist(e.world, s.anchor) / s.startDist, 0.01);
        this.ctx.setHint(`Scale: ×${s.factor.toFixed(3)} — type an exact factor ⏎`);
        this.ctx.requestRender();
        break;
      }
    }
  }

  pointerUp(e: ToolPointerEvent): void {
    const doc = this.ctx.store.doc;
    if (this.state.mode === "wallVertex") {
      const s = this.state;
      this.state = { mode: "idle" };
      this.ctx.gestureActive(false);
      this.ctx.setCursor(null);
      this.ctx.setEditing([]);
      const same = s.to.every((q, i) => Math.abs(q.x - s.from[i]!.x) < 1e-9 && Math.abs(q.y - s.from[i]!.y) < 1e-9);
      if (s.moved && !same) {
        const r = setWallPointsCommand(doc, s.wallId, s.to, "wall");
        if (typeof r === "string") this.ctx.setHint(r);
        else {
          this.ctx.store.dispatch(r);
          this.ctx.setHint(
            s.on === "wall" ? "Wall changed. Its end joins the other wall (a T)." : s.on === "end" ? "Wall changed. Its end meets the end of the other wall (a mitre)." : "Wall changed."
          );
        }
      } else this.syncHint();
      this.ctx.requestRender();
      return;
    }
    if (this.state.mode === "door") {
      const s = this.state;
      this.state = { mode: "idle" };
      this.ctx.gestureActive(false);
      this.ctx.setCursor(null);
      this.ctx.setEditing([]);
      if (s.moved && Math.abs(s.at - s.at0) > 1e-6) {
        const r = updateDoorCommand(doc, s.wallId, s.doorId, { at: s.at });
        if (typeof r === "string") this.ctx.setHint(r);
        else {
          this.ctx.store.dispatch(r);
          this.ctx.setHint("Door moved. Drag it again, or type the exact place in the Inspector (From the corner).");
        }
      }
      this.ctx.requestRender();
      return;
    }
    switch (this.state.mode) {
      case "move": {
        const d = this.state.delta;
        const k = this.state.scale ?? 1;
        const grab = this.state.start;
        this.ctx.setEditing([]);
        this.ctx.gestureActive(false);
        this.ctx.setSnapBase(null);
        this.state = { mode: "idle" };
        if (Math.abs(k - 1) > 1e-9) {
          // 3.20.0: moved AND scaled about the grab point (which sits under the pointer): one step
          const ids = e.ctrl ? null : this.ctx.selection.all;
          if (ids) this.ctx.store.dispatch({ kind: "updateEntities", patches: movedAndScaled(doc, ids, grab, d, k) });
          else {
            const dup = duplicateCommand(doc, this.ctx.selection.all, { x: 0, y: 0 });
            if (dup) {
              const draft = structuredClone(doc);
              applyCommand(draft, dup.cmd);
              this.ctx.store.dispatch({ kind: "batch", label: "copy and scale", commands: [dup.cmd, { kind: "updateEntities", patches: movedAndScaled(draft, dup.newIds, grab, d, k) }] });
              this.ctx.selection.set(dup.newIds);
            }
          }
          this.ctx.setHint(`Placed at ${(k * 100).toFixed(1)} %.`);
          this.ctx.requestRender();
          break;
        }
        if (Math.hypot(d.x, d.y) > 1e-9) {
          if (e.ctrl) {
            const dup = duplicateCommand(doc, this.ctx.selection.all, d);
            if (dup) {
              this.ctx.store.dispatch(dup.cmd);
              this.ctx.selection.set(dup.newIds);
            }
          } else {
            this.ctx.store.dispatch(moveCommand(doc, this.ctx.selection.all, d));
          }
        }
        this.syncHint();
        this.ctx.requestRender();
        break;
      }
      case "marquee": {
        const s = this.state;
        this.ctx.gestureActive(false);
        this.state = { mode: "idle" };
        if (dist(e.screen, s.startScreen) < 4) {
          // zero-drag = click on empty (already cleared in pointerDown unless shift)
          this.ctx.requestRender();
          this.syncHint();
          return;
        }
        const rect = {
          minX: Math.min(s.start.x, s.current.x),
          minY: Math.min(s.start.y, s.current.y),
          maxX: Math.max(s.start.x, s.current.x),
          maxY: Math.max(s.start.y, s.current.y)
        };
        const mode = e.screen.x >= s.startScreen.x ? "window" : "crossing";
        // while editing a group, never marquee-select the group you're inside
        const ids = marqueeTest(this.hitCtx(), rect, mode).filter((id) => id !== this.editingGroup());
        if (e.shift) {
          const merged = new Set(this.ctx.selection.all);
          for (const id of ids) merged.add(id);
          this.ctx.selection.set(merged);
        } else {
          this.ctx.selection.set(ids);
        }
        this.syncHint();
        this.ctx.requestRender();
        break;
      }
      case "grip": {
        const s = this.state;
        this.ctx.setEditing([]);
        this.ctx.gestureActive(false);
        this.ctx.setSnapBase(null);
        this.state = { mode: "idle" };
        const patch = diffEntity(s.original, s.preview);
        if (patch) {
          this.ctx.store.dispatch({ kind: "updateEntities", patches: [patch] });
        }
        this.ctx.requestRender();
        break;
      }
      case "edge":
        this.commitEdgeDrag(this.state);
        break;
      case "rotate":
      case "scale":
        this.commitTransform();
        break;
      case "pivot": {
        const s = this.state;
        this.ctx.gestureActive(false);
        this.ctx.setCursor(null);
        this.state = { mode: "idle" };
        const patch = setPivotPatch(doc, s.entityId, s.p);
        if (patch) this.ctx.store.dispatch({ kind: "updateEntities", patches: [patch] });
        this.ctx.setHint("Turning point set. Q, the Rotation box and the rotate knob turn about it.");
        this.ctx.requestRender();
        break;
      }
      case "pipeEnd":
        this.commitPipeEnd(this.state);
        break;
      case "maybeDrag":
        this.state = { mode: "idle" };
        break;
      case "idle":
        break;
    }
  }

  /** The free fitting or pipe end a dragged pipe end would attach to (its own old port counts as free). */
  private pipeTargetPort(s: Extract<DragState, { mode: "pipeEnd" }>, p: Vec): Port | null {
    const doc = this.ctx.store.doc;
    const meta = pipeMetaOf(doc.entities[s.entityId]);
    const own = meta ? (s.end === "a" ? meta.a : meta.b) : null;
    return freePortNear(
      doc,
      p,
      GRIP_PX / this.ctx.scale(),
      new Set([s.entityId]),
      (port) => own?.kind === "port" && own.ownerId === port.ownerId && own.port === port.index
    );
  }

  /** Release of a pipe end grip: attach to the end under it, or leave the end free at the point. */
  private commitPipeEnd(s: Extract<DragState, { mode: "pipeEnd" }>): void {
    this.ctx.setEditing([]);
    this.ctx.gestureActive(false);
    this.ctx.setSnapBase(null);
    this.ctx.setCursor(null);
    this.ctx.setCursorTip(null);
    this.state = { mode: "idle" };
    const doc = this.ctx.store.doc;
    const meta = pipeMetaOf(doc.entities[s.entityId]);
    if (!meta || !s.moved) {
      this.syncHint();
      this.ctx.requestRender();
      return; // a click on the grip, no drag
    }
    const old = s.end === "a" ? meta.a : meta.b;
    const oldStyle = s.end === "a" ? meta.endA : meta.endB;
    const port = s.port;
    const same = port && old.kind === "port" && old.ownerId === port.ownerId && old.port === port.index;
    if (same) {
      this.syncHint();
      this.ctx.requestRender();
      return; // dropped back on the end it was attached to
    }
    const start = old.kind === "free" ? old.p : null;
    if (!port && start && dist(start, s.p) < 1e-9) {
      this.syncHint();
      this.ctx.requestRender();
      return; // no move
    }
    const anchor = port ? ({ kind: "port", ownerId: port.ownerId, port: port.index } as const) : ({ kind: "free", p: { ...s.p } } as const);
    // joined: the look of that end (3.15.6: a rubber joint on a pump, a circle on the top of a pump plan); freed: a
    // flat end (a joined end and a rubber joint end mean nothing on a free end)
    const style = port ? joinedEndStyle(port) : oldStyle === "none" || oldStyle === "flex" ? "flat" : oldStyle;
    const cmd = combine("move pipe end", [
      old.kind === "port" ? unjoinPatches(doc, old.ownerId, old.port) : null,
      port ? joinPatches(doc, port.ownerId, port.index) : null,
      setPipePatches(doc, s.entityId, s.end === "a" ? { a: anchor, endA: style } : { b: anchor, endB: style })
    ]);
    if (cmd) this.ctx.store.dispatch(cmd);
    this.ctx.setHint(port ? `Pipe end attached to the ${port.label}.` : "Pipe end moved. It is free.");
    this.ctx.requestRender();
  }

  doubleClick(e: ToolPointerEvent): void {
    if (this.state.mode !== "idle") return;
    const doc = this.ctx.store.doc;
    const hit = hitTest(this.hitCtx(), e.world, this.tol());
    const entity = hit ? doc.entities[hit] : undefined;
    if (entity && isComponent(entity)) {
      // 3.28.0 (owner, 2026-10-23): a double-click selects the component AND every part joined to it, the whole
      // pipeline, so it can go to another layer in one step. Locked parts stay out of the selection.
      const run = connectedRun(doc, entity.id);
      const free = run.filter((id) => id === entity.id || !isLocked(doc, id));
      this.ctx.selection.set(free);
      if (run.length > 1) {
        const pipes = free.filter((id) => doc.entities[id]?.meta?.pipe).length;
        const locked = run.length - free.length;
        this.ctx.setHint(
          `Selected the connected run: ${free.length} parts (${pipes} pipe${pipes === 1 ? "" : "s"}, ${free.length - pipes} other)${locked ? `; ${locked} locked part${locked === 1 ? "" : "s"} left out` : ""}. Right-click › Move to layer to change its layer. Click once to select one part.`
        );
      } else {
        this.ctx.setHint(`${ownerName(doc, entity.id)} is a component: its lines move as one. Nothing is joined to it. Change it in the Inspector. To edit its lines one by one, ungroup it (Ctrl+Shift+G).`);
      }
      this.ctx.requestRender();
      return;
    }
    if (entity?.type === "group" && !isLocked(doc, entity.id)) {
      this.enterGroup(entity.id); // enters nested groups one level at a time too
      return;
    }
    if (this.editingGroup() && (!hit || !this.isGroupMember(hit))) {
      this.exitGroup(); // double-click outside the group = done
    }
  }

  /**
   * 3.20.0 (owner, 2026-10-21: "make the drag tool on images also allow for scale the image while it's being dragged and
   * the pointer is the centre of the scale so I can position and fit it on a drawing and match the sizing"). While an
   * image is dragged, the wheel scales it about the point of the image under the pointer: up = larger, down = smaller,
   * 2 % a notch, Shift = 0.2 %. The drop places it moved and scaled, one undo step.
   */
  wheel(e: { deltaY: number; shift: boolean }): boolean {
    if (this.state.mode !== "move" || !this.draggingImage()) return false;
    const step = e.shift ? 1.002 : 1.02;
    const next = (this.state.scale ?? 1) * Math.pow(step, -e.deltaY / 100);
    this.state.scale = Math.min(100, Math.max(0.01, next));
    this.syncScaleHint();
    this.ctx.requestRender();
    return true;
  }

  /** True when the selection being dragged has a picture in it. */
  private draggingImage(): boolean {
    const doc = this.ctx.store.doc;
    return [...expandGroupChildren(doc, this.ctx.selection.all)].some((id) => doc.entities[id]?.type === "image");
  }

  private syncScaleHint(): void {
    if (this.state.mode !== "move") return;
    const k = this.state.scale ?? 1;
    this.ctx.setHint(`Move and scale: ${(k * 100).toFixed(1)} % · wheel = scale about the pointer (Shift = fine) · + / - keys too · release to place · Esc = cancel`);
  }

  key(e: ToolKeyEvent): boolean {
    const doc = this.ctx.store.doc;
    // 3.20.0: + and - scale a dragged image too (for a trackpad), 1 % a press, Shift = 0.1 %
    if (this.state.mode === "move" && this.draggingImage() && (e.key === "+" || e.key === "=" || e.key === "-" || e.key === "_")) {
      const up = e.key === "+" || e.key === "=";
      const step = e.shift ? 1.001 : 1.01;
      this.state.scale = Math.min(100, Math.max(0.01, (this.state.scale ?? 1) * (up ? step : 1 / step)));
      this.syncScaleHint();
      this.ctx.requestRender();
      return true;
    }
    const pick = this.ctx.selectedDoor();
    if (e.key === "Escape") {
      if (this.state.mode !== "idle") {
        this.cancel();
        return true;
      }
      // 3.21.0: the first Esc drops the picked door; the wall stays selected
      if (pick) {
        this.ctx.selectDoor(null);
        this.syncHint();
        return true;
      }
      if (this.ctx.selection.size > 0) {
        this.ctx.selection.clear();
        this.syncHint();
        this.ctx.requestRender();
        return true;
      }
      if (this.editStack.length > 0) {
        this.exitGroup();
        return true;
      }
      return false;
    }
    // 3.21.0: Delete with a door picked takes the door away, not the wall
    if ((e.key === "Delete" || e.key === "Backspace") && pick && this.state.mode === "idle") {
      if (isLocked(doc, pick.wallId)) {
        this.ctx.setHint("Locked — unlock via right-click or the Layers panel (EC-TL-15)");
        return true;
      }
      const r = removeDoorCommand(doc, pick.wallId, pick.doorId);
      if (typeof r === "string") this.ctx.setHint(r);
      else {
        this.ctx.store.dispatch(r);
        this.ctx.selectDoor(null);
        this.ctx.setHint("Door taken away. The wall is whole there again. Ctrl+Z puts it back.");
      }
      return true;
    }
    if ((e.key === "Delete" || e.key === "Backspace") && this.ctx.selection.size > 0) {
      const ids = [...this.ctx.selection.all].filter((id) => !isLocked(doc, id));
      if (ids.length === 0) {
        this.ctx.setHint("Locked — unlock via right-click or the Layers panel (EC-TL-15)");
        return true;
      }
      // edit-in-place: children delete through the group-aware batch (keeps the
      // group's children array consistent and undo exact)
      const group = this.editingGroup();
      const members = group ? ids.filter((id) => this.isGroupMember(id)) : [];
      if (group && members.length > 0) {
        this.ctx.store.dispatch(makeDeleteChildrenCommand(doc, group, members));
        const rest = ids.filter((id) => !members.includes(id));
        if (rest.length > 0) this.ctx.store.dispatch(makeDeleteCommand(this.ctx.store.doc, rest));
        if (!this.ctx.store.doc.entities[group]) this.editStack = []; // group dissolved
        this.syncHint();
        return true;
      }
      this.ctx.store.dispatch(makeDeleteCommand(doc, ids));
      this.syncHint();
      return true;
    }
    const arrows: Record<string, Vec> = {
      ArrowLeft: { x: -1, y: 0 },
      ArrowRight: { x: 1, y: 0 },
      ArrowUp: { x: 0, y: -1 },
      ArrowDown: { x: 0, y: 1 }
    };
    const dir = arrows[e.key];
    if (dir && this.ctx.selection.size > 0) {
      const unlocked = [...this.ctx.selection.all].filter((id) => !isLocked(doc, id));
      if (unlocked.length === 0) return true;
      const step = this.ctx.minorStep() * (e.shift ? 10 : 1); // TL-5 nudge, Shift = ×10
      this.ctx.store.dispatch(moveCommand(doc, unlocked, { x: dir.x * step, y: dir.y * step }));
      return true;
    }
    return false;
  }

  /** TL-5a: typed distance during a wall slide/extrude = exact perpendicular offset.
   *  Transform frame: typed angle (rotate) or factor (scale) = exact transform. */
  measurement(raw: string): boolean {
    if (this.state.mode === "rotate") {
      const deg = Number.parseFloat(raw);
      if (!Number.isFinite(deg)) return false;
      this.state.deg = deg;
      this.commitTransform();
      return true;
    }
    if (this.state.mode === "scale") {
      const f = Number.parseFloat(raw);
      if (!Number.isFinite(f) || f <= 0) return false;
      this.state.factor = f;
      this.commitTransform();
      return true;
    }
    if (this.state.mode !== "edge" || this.state.kind === "free") return false;
    const { units } = this.ctx.docUnits();
    const value = parseScalar(raw, units);
    if (value === null) return false;
    const s = this.state;
    const sign = s.offset === 0 ? 1 : Math.sign(s.offset);
    this.applyEdgeDelta(s, { x: s.normal.x * sign * value, y: s.normal.y * sign * value });
    this.commitEdgeDrag(s);
    return true;
  }

  // ---- grip math ----------------------------------------------------------

  private applyGrip(s: Extract<DragState, { mode: "grip" }>, p: Vec, shift = false, snap?: SnapResult): void {
    const e = s.preview;
    const g = s.grip;
    switch (e.type) {
      case "dimension": {
        const orig = s.original;
        if (orig.type !== "dimension") break;
        const doc = this.ctx.store.doc;
        const og = dimensionGeometry(doc, orig);
        if (!og) break;
        if (g.kind === "dimLine") {
          e.offset = offsetFor(orig.orient, og.a, og.b, p);
        } else if (g.kind === "dimA" || g.kind === "dimB") {
          // a snap to a vertex, centre or end keeps the dimension attached to that geometry
          const anchor = snap && dist(snap.point, p) < 1e-9 ? anchorFromSnap(doc, snap) : ({ kind: "free", p: { ...p } } as const);
          if (g.kind === "dimA") e.a = anchor;
          else e.b = anchor;
          if (orig.kind === "linear") {
            // the dimension line stays where it was: re-measure its offset from the new points
            const na = resolveAnchor(doc, e.a) ?? og.a;
            const nb = resolveAnchor(doc, e.b) ?? og.b;
            e.offset = offsetFor(orig.orient, na, nb, og.textPos);
          }
        }
        break;
      }
      case "label": {
        if (g.kind === "labelHead") e.head = p;
        else if (g.kind === "labelTail") e.tail = p;
        break;
      }
      case "grid": {
        const orig = s.original;
        if (orig.type !== "grid" || g.kind !== "gridCorner") break;
        const anchorIdx = (g.i + 2) % 4;
        const w0 = orig.cols * orig.cellW;
        const h0 = orig.rows * orig.cellH;
        const anchors: Vec[] = [
          orig.pos,
          { x: orig.pos.x + w0, y: orig.pos.y },
          { x: orig.pos.x + w0, y: orig.pos.y + h0 },
          { x: orig.pos.x, y: orig.pos.y + h0 }
        ];
        const anchor = anchors[anchorIdx]!;
        const w = Math.max(Math.abs(p.x - anchor.x), 1);
        const h = Math.max(Math.abs(p.y - anchor.y), 1);
        if (orig.lockAspect) {
          // ANN-6: cells fixed → counts recompute, extent snaps to whole cells
          e.cols = Math.min(1000, Math.max(1, Math.round(w / orig.cellW)));
          e.rows = Math.min(1000, Math.max(1, Math.round(h / orig.cellH)));
        } else {
          e.cellW = w / orig.cols;
          e.cellH = h / orig.rows;
        }
        const ew = e.cols * e.cellW;
        const eh = e.rows * e.cellH;
        e.pos = {
          x: Math.min(anchor.x, anchor.x + Math.sign(p.x - anchor.x || 1) * ew),
          y: Math.min(anchor.y, anchor.y + Math.sign(p.y - anchor.y || 1) * eh)
        };
        break;
      }
      case "image": {
        const orig = s.original;
        if (orig.type !== "image") break;
        if (g.kind === "imgRotate") {
          const c = imageCenter(orig);
          let deg = angleOf(sub(p, c)) + 90; // grip sits at -90° from center
          if (shift) deg = Math.round(deg / 15) * 15; // TL-5 rotate snap
          e.rotationDeg = normalizeDeg(deg);
          break;
        }
        if (g.kind === "imgCorner") {
          // scale about the OPPOSITE corner; aspect locked unless Shift (TL-5/IMG-6)
          const corners = imageCorners(orig);
          const anchor = corners[(g.i + 2) % 4]!;
          const u = dir(orig.rotationDeg);
          const v = dir(orig.rotationDeg + 90);
          const lx = Math.abs((p.x - anchor.x) * u.x + (p.y - anchor.y) * u.y);
          const ly = Math.abs((p.x - anchor.x) * v.x + (p.y - anchor.y) * v.y);
          let w = Math.max(lx, 0.1);
          let h = Math.max(ly, 0.1);
          if (!shift) {
            const k = Math.max(w / orig.wMm, h / orig.hMm);
            w = orig.wMm * k;
            h = orig.hMm * k;
          }
          // keep the anchor corner fixed: local offset of the dragged corner from the anchor
          const sxAnchor = (g.i + 2) % 4; // anchor corner index
          const localX = sxAnchor === 1 || sxAnchor === 2 ? -w : w; // direction anchor → dragged
          const localY = sxAnchor === 2 || sxAnchor === 3 ? -h : h;
          const centerLocal = { x: localX / 2, y: localY / 2 };
          const center = {
            x: anchor.x + centerLocal.x * u.x + centerLocal.y * v.x,
            y: anchor.y + centerLocal.x * u.y + centerLocal.y * v.y
          };
          e.wMm = w;
          e.hMm = h;
          e.pos = { x: center.x - w / 2, y: center.y - h / 2 };
          break;
        }
        break;
      }
      case "polyline": {
        const orig = s.original as PolylineEntity;
        if (g.kind === "vertex") {
          e.points = orig.points.map((q, i) => (i === g.i ? p : q));
        } else if (g.kind === "midInsert") {
          const pts = [...orig.points];
          pts.splice(g.i + 1, 0, p);
          e.points = pts;
        }
        break;
      }
      case "circle": {
        if (g.kind === "center") e.center = p;
        else if (g.kind === "radius") e.r = Math.max(dist(p, e.center), 1e-6);
        break;
      }
      case "arc": {
        const orig = s.original;
        if (orig.type !== "arc") break;
        if (g.kind === "center") {
          e.center = p;
        } else if (g.kind === "arcStart" || g.kind === "arcEnd") {
          const newDeg = angleOf(sub(p, orig.center));
          if (g.kind === "arcStart") {
            const end = orig.startDeg + orig.sweepDeg;
            e.startDeg = newDeg;
            e.sweepDeg = orig.sweepDeg >= 0 ? normalizeDeg(end - newDeg) : normalizeDeg(end - newDeg) - 360;
          } else {
            const rel = normalizeDeg(newDeg - orig.startDeg);
            e.sweepDeg = orig.sweepDeg >= 0 ? rel : rel - 360;
          }
          if (Math.abs(e.sweepDeg) < 1e-6) e.sweepDeg = orig.sweepDeg; // INV-5 guard
        } else if (g.kind === "arcBulge") {
          const a = arcPoint(orig.center, orig.r, orig.startDeg);
          const b = arcPoint(orig.center, orig.r, orig.startDeg + orig.sweepDeg);
          const np = arcFrom3Points(a, b, p);
          if (np) {
            e.center = np.center;
            e.r = np.r;
            e.startDeg = np.startDeg;
            e.sweepDeg = np.sweepDeg;
          }
        }
        break;
      }
      default:
        break;
    }
  }

  /** Alt+click a vertex deletes it (TL-5), guarded by INV-4 minimum counts (EC-TL-3). */
  tryVertexDelete(e: ToolPointerEvent): boolean {
    if (!e.alt) return false;
    const g = this.gripAtWorld(e.world);
    if (!g) return false;
    const grip = g.grip;
    if (grip.kind !== "vertex") return false;
    const doc = this.ctx.store.doc;
    const entity = doc.entities[g.entityId];
    if (!entity || entity.type !== "polyline") return false;
    const min = entity.closed ? 3 : 2;
    if (entity.points.length <= min) {
      this.ctx.setHint("Can't remove — a line needs at least two points (EC-TL-3)");
      return true;
    }
    const pts = entity.points.filter((_, i) => i !== grip.i);
    this.ctx.store.dispatch({
      kind: "updateEntities",
      patches: [{ id: entity.id, before: { points: entity.points }, after: { points: pts } }]
    });
    return true;
  }

  // ---- rendering -----------------------------------------------------------

  renderDynamic(v: RenderView): void {
    const doc = this.ctx.store.doc;
    const st = this.state;
    // 3.21.0: a door being dragged. The wall is drawn here, with the door at the place it goes (the static layer
    // leaves the wall out meanwhile), in the wall's own look.
    if ((st.mode === "door" || st.mode === "wallVertex") && st.moved) {
      const g = doc.entities[st.wallId];
      const w = wallMetaOf(g);
      const spine = spineOf(doc, st.wallId);
      if (g?.type === "group" && w && spine) {
        const layer = doc.layers.find((l) => l.id === g.layerId);
        // 3.23.0: drawn with its joins (the walls round it as they are)
        const override = st.mode === "door" ? { points: spine.points, doors: w.doors.map((d) => (d.id === st.doorId ? { ...d, at: st.at } : d)) } : { points: st.to };
        const base = { id: `${st.wallId}-preview`, layerId: g.layerId, locked: false, hidden: false };
        for (const p of wallPartsInDoc(doc, st.wallId, override)) {
          const e = p.type === "arc"
            ? { ...base, type: "arc" as const, center: p.center, r: p.r, startDeg: p.startDeg, sweepDeg: p.sweepDeg, style: p.style }
            : { ...base, type: "polyline" as const, points: p.points, closed: p.closed, style: p.style };
          drawEntity(v, e as Entity, layer, doc);
        }
      }
      return;
    }
    if (st.mode === "rotate" || st.mode === "scale") {
      // exact live ghost via a canvas transform about the pivot/anchor
      const { ctx } = v;
      ctx.save();
      if (st.mode === "rotate") {
        ctx.translate(st.pivot.x, st.pivot.y);
        ctx.rotate((st.deg * Math.PI) / 180);
        ctx.translate(-st.pivot.x, -st.pivot.y);
      } else {
        ctx.translate(st.anchor.x, st.anchor.y);
        ctx.scale(st.factor, st.factor);
        ctx.translate(-st.anchor.x, -st.anchor.y);
      }
      ctx.globalAlpha *= 0.85;
      for (const id of expandGroupChildren(doc, this.ctx.selection.all)) {
        const entity = doc.entities[id];
        if (!entity || entity.type === "group" || entity.hidden) continue;
        const layer = doc.layers.find((l) => l.id === entity.layerId);
        if (entity.type === "image") drawImageEntity(v, entity, layer, this.ctx.assets);
        else drawEntity(v, entity, layer, doc);
      }
      ctx.restore();
      return;
    }
    if (st.mode === "move") {
      for (const g of st.guides ?? []) {
        const { ctx } = v;
        ctx.save();
        ctx.strokeStyle = GUIDE;
        ctx.fillStyle = GUIDE;
        ctx.lineWidth = 1.5 / v.cam.scale;
        if (g.kind === "connect") {
          ctx.beginPath();
          ctx.arc(g.a.x, g.a.y, 7 / v.cam.scale, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          ctx.setLineDash([6 / v.cam.scale, 4 / v.cam.scale]);
          // a centre line runs on past both ends, so it reads as a line, not a gap
          const ux = g.b.x - g.a.x, uy = g.b.y - g.a.y;
          const len = Math.hypot(ux, uy) || 1;
          const ext = (g.kind === "axis" ? 40 : 15) / v.cam.scale;
          ctx.beginPath();
          ctx.moveTo(g.a.x - (ux / len) * ext, g.a.y - (uy / len) * ext);
          ctx.lineTo(g.b.x + (ux / len) * ext, g.b.y + (uy / len) * ext);
          ctx.stroke();
          ctx.setLineDash([]);
          for (const p of [g.a, g.b]) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, 3 / v.cam.scale, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.restore();
      }
      const patches = translatePatches(doc, this.ctx.selection.all, st.delta);
      const k = st.scale ?? 1;
      const at = { x: st.start.x + st.delta.x, y: st.start.y + st.delta.y };
      v.ctx.save();
      if (k !== 1) {
        v.ctx.translate(at.x, at.y);
        v.ctx.scale(k, k);
        v.ctx.translate(-at.x, -at.y);
      }
      for (const patch of patches) {
        const entity = doc.entities[patch.id];
        if (!entity || entity.hidden) continue;
        const ghost = { ...structuredClone(entity), ...patch.after } as Entity;
        const layer = doc.layers.find((l) => l.id === entity.layerId);
        if (ghost.type === "image") drawImageEntity(v, ghost, layer, this.ctx.assets);
        else drawEntity(v, ghost, layer, doc);
      }
      v.ctx.restore();
      if (k !== 1) drawReadout(v, at, `${(k * 100).toFixed(1)} %`);
    } else if (st.mode === "pipeEnd") {
      drawPipeGhost(v, st.fixed, st.p, st.size, "#4d9fe1");
    } else if (st.mode === "grip" || st.mode === "edge") {
      const layer = doc.layers.find((l) => l.id === st.preview.layerId);
      if (st.preview.type === "image") drawImageEntity(v, st.preview, layer, this.ctx.assets);
      else drawEntity(v, st.preview, layer, doc);
      if (st.mode === "edge") {
        // emphasize the wall being slid (in extrude previews it sits one index later)
        const pts = st.preview.points;
        const extruded = st.kind === "extrude" && pts.length > st.original.points.length;
        const i0 = extruded ? st.segIndex + 1 : st.segIndex;
        const a = pts[i0]!;
        const b = pts[(i0 + 1) % pts.length]!;
        v.ctx.strokeStyle = "#4d9fe1";
        v.ctx.lineWidth = 3 / v.cam.scale;
        v.ctx.beginPath();
        v.ctx.moveTo(a.x, a.y);
        v.ctx.lineTo(b.x, b.y);
        v.ctx.stroke();
      }
    }
  }

  renderOverlay(v: RenderView): void {
    const { ctx } = v;
    const px = (n: number): number => n / v.cam.scale;

    // 3.23.0: a wall end or corner being dragged: the length of the walls next to it
    if (this.state.mode === "wallVertex" && this.state.moved) {
      const s = this.state;
      const n = s.to.length;
      const { units, precision } = this.ctx.docUnits();
      const lenOf = (i: number) => Math.hypot(s.to[(i + 1) % n]!.x - s.to[i]!.x, s.to[(i + 1) % n]!.y - s.to[i]!.y);
      const segs = [s.k - 1, s.k].filter((i) => (s.closed ? true : i >= 0 && i < n - 1)).map((i) => (i + n) % n);
      const words = segs.map((i) => `wall ${i + 1}: ${formatLength(lenOf(i), units, precision)}`).join(" · ");
      drawReadout(v, s.p, s.on ? `${words} · joins ${s.on === "end" ? "the end" : "the wall"}` : words);
    }
    // 3.21.0: the picked door, and while it is dragged, where it goes and how far it is from the corner
    const pick = this.ctx.selectedDoor();
    if (pick) {
      const st = this.state.mode === "door" && this.state.moved && this.state.doorId === pick.doorId ? this.state : null;
      const mark = (pts: Vec[] | null, dash: number[]) => {
        if (!pts) return;
        ctx.save();
        ctx.strokeStyle = DOOR_MARK;
        ctx.lineWidth = px(2);
        ctx.setLineDash(dash);
        ctx.beginPath();
        pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.closePath();
        ctx.stroke();
        ctx.restore();
      };
      const doc = this.ctx.store.doc;
      mark(doorOutline(doc, pick.wallId, pick.doorId, st ? st.at : undefined), [px(6), px(4)]);
      if (st) {
        const d = wallMetaOf(doc.entities[pick.wallId])?.doors.find((x) => x.id === pick.doorId);
        if (d) {
          const { units, precision } = this.ctx.docUnits();
          drawReadout(v, st.p, `from the corner ${formatLength(st.at - d.width / 2, units, precision)}`);
        }
      }
    }

    // group edit-in-place: persistent accent frame around the group being edited
    const editing = this.editingGroup();
    if (editing) {
      const g = this.ctx.store.doc.entities[editing];
      if (g) {
        const b = entityBounds(g, this.ctx.store.doc);
        if (b.minX <= b.maxX) {
          const pad = px(10);
          ctx.strokeStyle = "#4dd0e1";
          ctx.lineWidth = px(1.5);
          ctx.setLineDash([px(10), px(5)]);
          ctx.strokeRect(b.minX - pad, b.minY - pad, b.maxX - b.minX + pad * 2, b.maxY - b.minY + pad * 2);
          ctx.setLineDash([]);
        }
      }
    }

    // hover pre-highlight
    if (this.hoverId && !this.ctx.selection.has(this.hoverId) && this.state.mode === "idle") {
      outlineEntity(v, this.ctx.store.doc, this.hoverId, "rgba(77, 159, 225, 0.5)", px(2));
    }

    // transform frame: dashed padded bbox + corner scale handles + rotate knob
    const inTransform = this.state.mode === "rotate" || this.state.mode === "scale";
    const frame = this.state.mode === "idle" || this.state.mode === "maybeDrag" ? this.transformFrame() : null;
    if (frame) {
      const size = px(GRIP_PX * 0.9);
      ctx.strokeStyle = "rgba(77,159,225,0.65)";
      ctx.lineWidth = px(1);
      ctx.setLineDash([px(4), px(4)]);
      ctx.strokeRect(
        frame.corners[0]!.x,
        frame.corners[0]!.y,
        frame.corners[2]!.x - frame.corners[0]!.x,
        frame.corners[2]!.y - frame.corners[0]!.y
      );
      ctx.setLineDash([]);
      // rotate knob + stem
      const topMid = { x: (frame.corners[0]!.x + frame.corners[1]!.x) / 2, y: frame.corners[0]!.y };
      ctx.beginPath();
      ctx.moveTo(topMid.x, topMid.y);
      ctx.lineTo(frame.rotP.x, frame.rotP.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(frame.rotP.x, frame.rotP.y, size / 2, 0, Math.PI * 2);
      ctx.fillStyle = "#4d9fe1";
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.stroke();
      // corner scale handles
      for (const c of frame.corners) {
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#4d9fe1";
        ctx.fillRect(c.x - size / 2, c.y - size / 2, size, size);
        ctx.strokeRect(c.x - size / 2, c.y - size / 2, size, size);
      }
    }

    // turning point: orange when set, blue when the object turns about its centre
    const st = this.state;
    const pivotAt = st.mode === "pivot" ? { p: st.p, own: true } : st.mode === "rotate" ? { p: st.pivot, own: this.selectedOwnPivot() !== null } : frame && this.ctx.selection.size === 1 ? { p: frame.pivot, own: frame.ownPivot } : null;
    if (pivotAt) drawPivotMarker(v, pivotAt.p, pivotAt.own);

    // the end a dragged pipe end would attach to
    if (st.mode === "pipeEnd" && st.port) drawPorts(v, [st.port], st.port);

    // grips
    const g = this.state.mode === "grip" || this.state.mode === "pipeEnd" || inTransform ? null : this.grips();
    if (g) {
      const size = px(GRIP_PX);
      // 3.23.0: while a wall end or corner is dragged, the grips of that wall are where the drag puts them
      const live = this.state.mode === "wallVertex" && this.state.moved ? this.state.to : null;
      for (const g0 of g.grips) {
        const grip = live && g0.kind === "wallVertex" && live[g0.i] ? { ...g0, p: live[g0.i]! } : g0;
        if (grip.kind === "pipeEnd") {
          // round grips for pipe ends (square = a point of a line)
          ctx.fillStyle = "#4d9fe1";
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = px(1.5);
          ctx.beginPath();
          ctx.arc(grip.p.x, grip.p.y, size / 2 + px(1), 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          continue;
        }
        ctx.fillStyle = grip.kind === "midInsert" ? "rgba(77,159,225,0.5)" : "#4d9fe1";
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = px(1);
        const half = grip.kind === "midInsert" ? size / 3 : size / 2;
        ctx.fillRect(grip.p.x - half, grip.p.y - half, half * 2, half * 2);
        ctx.strokeRect(grip.p.x - half, grip.p.y - half, half * 2, half * 2);
      }
    }

    // marquee
    if (this.state.mode === "marquee") {
      const s = this.state;
      const window = worldToScreen(v.cam, v.cssW, v.cssH, s.current).x >= s.startScreen.x;
      ctx.strokeStyle = window ? "#4d9fe1" : "#4caf7d";
      ctx.fillStyle = window ? "rgba(77,159,225,0.08)" : "rgba(76,175,125,0.08)";
      ctx.lineWidth = px(1);
      ctx.setLineDash(window ? [] : [px(4), px(3)]);
      const x = Math.min(s.start.x, s.current.x);
      const y = Math.min(s.start.y, s.current.y);
      const w = Math.abs(s.current.x - s.start.x);
      const h = Math.abs(s.current.y - s.start.y);
      ctx.fillRect(x, y, w, h);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }
}

/** The turning-point marker: a ring with a cross. Orange = a set point, blue = the box centre. */
export function drawPivotMarker(v: RenderView, p: Vec, own: boolean): void {
  const { ctx } = v;
  const px = (n: number): number => n / v.cam.scale;
  const colour = own ? "#f0a030" : "#4d9fe1";
  ctx.save();
  ctx.setLineDash([]);
  ctx.lineWidth = px(2);
  ctx.strokeStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(p.x, p.y, px(6), 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = px(1.5);
  ctx.strokeStyle = colour;
  ctx.beginPath();
  ctx.arc(p.x, p.y, px(6), 0, Math.PI * 2);
  ctx.moveTo(p.x - px(10), p.y);
  ctx.lineTo(p.x + px(10), p.y);
  ctx.moveTo(p.x, p.y - px(10));
  ctx.lineTo(p.x, p.y + px(10));
  ctx.stroke();
  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.arc(p.x, p.y, px(1.8), 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** TL-5a: segment of a polyline within tol of p — index + unit normal (perp of direction). */
function nearestSegment(
  entity: PolylineEntity,
  p: Vec,
  tol: number
): { index: number; normal: Vec } | null {
  const pts = entity.points;
  const segCount = pts.length - 1 + (entity.closed && pts.length > 2 ? 1 : 0);
  let best: { index: number; normal: Vec; d: number } | null = null;
  for (let i = 0; i < segCount; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    const len = dist(a, b);
    if (len < 1e-9) continue;
    const d = distPointSeg(p, a, b);
    if (d <= tol && (!best || d < best.d)) {
      best = { index: i, normal: { x: -(b.y - a.y) / len, y: (b.x - a.x) / len }, d };
    }
  }
  return best ? { index: best.index, normal: best.normal } : null;
}

/** Field-level diff original→edited as one updateEntities patch (null when unchanged). */
function diffEntity(
  original: Entity,
  edited: Entity
): { id: string; before: Record<string, unknown>; after: Record<string, unknown> } | null {
  const before: Record<string, unknown> = {};
  const after: Record<string, unknown> = {};
  const a = original as unknown as Record<string, unknown>;
  const b = edited as unknown as Record<string, unknown>;
  for (const key of Object.keys(b)) {
    if (key === "id") continue;
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) {
      before[key] = structuredClone(a[key]);
      after[key] = structuredClone(b[key]);
    }
  }
  if (Object.keys(after).length === 0) return null;
  return { id: original.id, before, after };
}

/** Accent outline used for hover + selection highlight. */
export function outlineEntity(v: RenderView, doc: Doc, id: string, color: string, width: number): void {
  const entity = doc.entities[id];
  if (!entity) return;
  if (entity.type === "group") {
    // a hidden part (a pipe end that is joined, a collar of a pipe end that is not a floor penetration) has no outline
    for (const c of entity.children) if (!doc.entities[c]?.hidden) outlineEntity(v, doc, c, color, width);
    return;
  }
  const { ctx } = v;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash([]);
  if (entity.type === "label") {
    // the hover and selection mark is the part that selects it: the text (the leader moves by its end grips)
    const t = labelTextBox(entity);
    ctx.strokeRect(t.minX, t.minY, t.maxX - t.minX, t.maxY - t.minY);
    return;
  }
  if (tracePath(ctx, entity)) ctx.stroke();
}

/** Hover cursor per grip kind — the press affordance (grab/resize/move). */
function gripHoverCursor(kind: Grip["kind"]): string {
  switch (kind) {
    case "imgCorner":
    case "gridCorner":
      return "nwse-resize";
    case "labelHead":
    case "labelTail":
    case "dimLine":
      return "move";
    default:
      return "grab"; // vertex/midInsert/center/radius/arc/rotate grips
  }
}
